import {
  CreditCard,
  ChartBar,
  SignOut,
  ChatCircleText,
  FileText,
  Shield,
  Trash,
  Globe,
  CircleHalf,
  Tag,
  Crown,
} from '@phosphor-icons/react';

export interface AccountMenuItem {
  href: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string; weight?: 'regular' | 'fill' }>;
  /** Special item type for non-link actions */
  action?: 'logout';
  /**
   * Hide the row unless the user has the `white_label` entitlement
   * (Builder Pass / grandfathered Designer). Branding = white-label control,
   * so it only makes sense for those who can actually disable GetFacade
   * branding — everyone else never sees the row.
   */
  requiresWhiteLabel?: boolean;
}

export interface AccountMenuSection {
  titleKey?: string;
  items: AccountMenuItem[];
}

/**
 * Single source of truth for account settings menu structure.
 * Used by both the mobile account page and the desktop sidebar.
 */
/**
 * Hrefs excluded from prev/next pager navigation in the settings header.
 * Mirrors Apple's `AccountMenuItem.navigableItems` filter — privacy/terms
 * are external docs, delete is destructive, logout is an action.
 */
const NON_NAVIGABLE_HREFS: ReadonlySet<string> = new Set([
  '/account/privacy',
  '/account/terms',
  '/account/delete',
]);

export const accountMenuSections: AccountMenuSection[] = [
  {
    items: [
      { href: '/account/tokens', labelKey: 'tokenBalance', icon: Crown },
    ],
  },
  {
    titleKey: 'sections.settings',
    items: [
      { href: '/account/language', labelKey: 'settings.language', icon: Globe },
      { href: '/account/appearance', labelKey: 'settings.appearance', icon: CircleHalf },
      { href: '/account/branding', labelKey: 'settings.branding', icon: Tag, requiresWhiteLabel: true },
    ],
  },
  {
    titleKey: 'sections.support',
    items: [
      { href: '/account/feedback', labelKey: 'support.contactSupport', icon: ChatCircleText },
      { href: '/account/privacy', labelKey: 'support.privacyPolicy', icon: Shield },
      { href: '/account/terms', labelKey: 'support.termsOfService', icon: FileText },
    ],
  },
  {
    titleKey: 'sections.account',
    items: [
      { href: '/account/purchase-history', labelKey: 'subscriptionActions.purchaseHistory', icon: CreditCard },
      { href: '/account/usage-stats', labelKey: 'subscriptionActions.usageStatistics', icon: ChartBar },
      { href: '', labelKey: 'logout', icon: SignOut, action: 'logout' },
      { href: '/account/delete', labelKey: 'deleteAccount.title', icon: Trash },
    ],
  },
];

/**
 * Flat list of menu items participating in prev/next pager navigation.
 * Excludes logout action, privacy/terms (external docs), and delete (destructive).
 */
export const navigableAccountItems: AccountMenuItem[] = accountMenuSections
  .flatMap((section) => section.items)
  .filter((item) => item.action !== 'logout' && !NON_NAVIGABLE_HREFS.has(item.href));

/**
 * All linkable account menu items (excludes only the logout action).
 * Used by the layout header to resolve a page title from the current
 * pathname so each settings sub-page renders the same title shown in the
 * sidebar — matches Apple's `AccountMenuItem.displayName` lookup.
 */
export const allAccountLinkItems: AccountMenuItem[] = accountMenuSections
  .flatMap((section) => section.items)
  .filter((item) => item.action !== 'logout' && item.href.length > 0);

/**
 * Account sections with entitlement-gated rows filtered out for the current
 * user. Single source for both the mobile page and the desktop sidebar so the
 * Branding (white-label) row hides identically. Sections that empty out are
 * dropped so no bare header remains.
 */
export function visibleAccountMenuSections(opts: {
  canUseWhiteLabel: boolean;
}): AccountMenuSection[] {
  return accountMenuSections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.requiresWhiteLabel || opts.canUseWhiteLabel,
      ),
    }))
    .filter((section) => section.items.length > 0);
}

/**
 * Navigable (prev/next pager) account items with entitlement-gated rows
 * filtered out for the current user. Mirrors `visibleAccountMenuSections` so
 * the Branding (white-label) page drops out of the pager identically to the menu.
 */
export function visibleNavigableAccountItems(opts: {
  canUseWhiteLabel: boolean;
}): AccountMenuItem[] {
  return navigableAccountItems.filter(
    (item) => !item.requiresWhiteLabel || opts.canUseWhiteLabel,
  );
}
